import {
	isExpiringUrl,
	type PortalCatalogItemDto,
	type StorefrontSeoProductModel,
} from '@supply-mover-2-0/shared';
import { applyHeadlineToUnitPrice } from './promo-pricing/headlines';

/** The catalog item fields a product page's head tags are built from. */
export type StorefrontSeoItemSourceModel = Pick<
	PortalCatalogItemDto,
	| 'id'
	| 'description'
	| 'extendedDescription'
	| 'primaryImageUrl'
	| 'images'
	| 'externalId'
	| 'ndc'
	| 'manufacturer'
	| 'price'
	| 'contractPrice'
	| 'promoHeadline'
	| 'isPriced'
	| 'inStock'
>;

/**
 * PD-17174 — a storefront catalog item as the product page's head tags and
 * structured data describe it. Used by crm-api (for the HTML the edge serves
 * a crawler) and by the storefront (once the page has loaded), so both name,
 * describe and price the product the same way.
 *
 * The price is the one the product page shows (contract price, else list
 * price, with a promotion applied when it lowers it), and only when
 * `includePrice` says the visitor sees prices: the caller passes true only for
 * a guest on a storefront that shows guests prices, so a signed-in shopper's
 * contract price never reaches structured data. An unpriced item ("Call for
 * price") has no price.
 *
 * The picture is the first one with a lasting address. A picture stored in
 * Workd's own files is served through a presigned link that expires within
 * hours, and there is no public, unsigned address for it, so it is skipped:
 * search engines and chat apps keep an image URL far longer than that. Pictures
 * linked by URL (a CDN or the supplier's site) are kept. With none, the head
 * tags fall back to the brand's logo.
 */
export function toStorefrontSeoProduct(
	item: StorefrontSeoItemSourceModel,
	options: { includePrice: boolean },
): StorefrontSeoProductModel {
	return {
		id: item.id,
		name: item.description ?? item.externalId ?? String(item.id),
		description: item.extendedDescription ?? null,
		imageUrl: firstLastingImageUrl([
			item.primaryImageUrl,
			...item.images.map((image) => image.imageUrl),
		]),
		sku: item.externalId,
		ndc: item.ndc,
		brand: item.manufacturer,
		price: options.includePrice ? shownUnitPrice(item) : null,
		inStock: item.inStock,
	};
}

function shownUnitPrice(item: StorefrontSeoItemSourceModel): number | null {
	if (!item.isPriced) {
		return null;
	}
	const contract =
		item.contractPrice != null ? parseFloat(item.contractPrice) : Number.NaN;
	const list = item.price != null ? parseFloat(item.price) : Number.NaN;
	const base = Number.isFinite(contract)
		? contract
		: Number.isFinite(list)
			? list
			: Number.NaN;
	if (!Number.isFinite(base)) {
		return null;
	}
	const promo =
		item.promoHeadline != null
			? applyHeadlineToUnitPrice(base, item.promoHeadline)
			: null;
	return promo != null && promo < base ? promo : base;
}

function firstLastingImageUrl(
	urls: ReadonlyArray<string | null>,
): string | null {
	for (const url of urls) {
		if (url != null && url.trim() !== '' && !isExpiringUrl(url)) {
			return url;
		}
	}
	return null;
}
